import React, { useState } from 'react';
import { Sparkles, Check, X, ArrowRight, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { modelServicePost } from '../../../../utils/modelService';

/**
 * JPRO Intelligence prompt refiner.
 *
 * Two-stage smart flow (decided server-side):
 *   - Vague drafts -> backend returns clarifying questions -> modal asks user
 *   - Detailed drafts -> backend returns a refined prompt + notes -> accept/reject card
 *
 * Two ways to use this:
 *
 *   1. Self-contained component (Creative Studio pattern):
 *        <PromptRefiner draft={...} mode="creative_studio" onAccept={...} />
 *      Renders its own pill button, then swaps to modal/preview in place.
 *
 *   2. Hook (Unified Chat pattern):
 *        const refiner = usePromptRefiner({ draft, mode: 'chat', onAccept });
 *        // place refiner.modal and refiner.preview wherever fits the layout
 *        // call refiner.open() from a custom trigger (e.g. small icon next to send)
 */

export const usePromptRefiner = ({ draft, mode = 'creative_studio', aspectRatio, recentMessages, attachments, onAccept }) => {
  const [stage, setStage] = useState('idle');           // idle | questions | preview
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [previewData, setPreviewData] = useState(null); // { refined_prompt, notes, original_prompt }

  const refineMutation = useMutation({
    mutationFn: (payload) => modelServicePost('/refine-prompt', payload).then((r) => r.json()),
  });

  const reset = () => {
    setStage('idle');
    setQuestions([]);
    setAnswers({});
    setPreviewData(null);
    refineMutation.reset();
  };

  const callRefiner = async (extraPayload = {}) => {
    const resp = await refineMutation.mutateAsync({
      prompt: draft,
      mode,
      aspect_ratio: aspectRatio,
      recent_messages: recentMessages,
      attachments,
      ...extraPayload,
    });

    if (resp?.stage === 'questions' && Array.isArray(resp.questions) && resp.questions.length > 0) {
      const normalized = resp.questions
        .map((q) => (typeof q === 'string' ? { question: q, example: null } : q))
        .filter((q) => q && typeof q.question === 'string' && q.question.trim());
      setQuestions(normalized);
      setAnswers(Object.fromEntries(normalized.map((q) => [q.question, ''])));
      setStage('questions');
    } else if (resp?.refined_prompt) {
      setPreviewData({
        refined_prompt: resp.refined_prompt,
        notes: resp.notes || null,
        original_prompt: resp.original_prompt || draft,
      });
      setStage('preview');
    } else {
      throw new Error('Refiner returned an unexpected response shape');
    }
  };

  const open = async (extras = {}) => {
    if (!draft || !draft.trim()) return;
    try {
      await callRefiner(extras);
    } catch (e) {
      console.error('Refine failed:', e);
    }
  };

  const handleSubmitAnswers = async () => {
    try {
      await callRefiner({ answers });
    } catch (e) {
      console.error('Refine (with answers) failed:', e);
    }
  };

  const handleAccept = () => {
    if (!previewData) return;
    onAccept(previewData.refined_prompt, {
      originalPrompt: previewData.original_prompt,
      notes: previewData.notes,
    });
    reset();
  };

  const modal = stage === 'questions' ? (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold font-rasa text-gray-900 dark:text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-500" />
              A few quick questions
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Your draft was short. Answer what matters, skip the rest.
            </p>
          </div>
          <button onClick={reset} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-rasa">Your draft</div>
          <p className="text-sm text-gray-700 dark:text-gray-200 italic border-l-2 border-blue-300 dark:border-blue-700 pl-3">
            {draft}
          </p>

          <div className="space-y-3 pt-2">
            {questions.map((q) => {
              const placeholder = q.example
                ? `e.g. ${q.example}`
                : 'Leave blank to skip';
              return (
                <div key={q.question}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 font-rasa mb-1">
                    {q.question}
                  </label>
                  <input
                    type="text"
                    value={answers[q.question] || ''}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.question]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600
                               bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                               placeholder-gray-400 dark:placeholder-gray-500 placeholder:italic
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={reset}
            className="px-3 py-1.5 text-sm rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitAnswers}
            disabled={refineMutation.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {refineMutation.isPending ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Refining…
              </>
            ) : (
              <>
                Refine prompt
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const preview = stage === 'preview' && previewData ? (
    <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-gradient-to-r from-blue-50 to-orange-50/50 dark:from-blue-900/20 dark:to-orange-900/10 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide font-rasa text-blue-700 dark:text-blue-300">
          <Sparkles className="w-3.5 h-3.5" />
          JPRO Intelligence suggests
        </div>
        <button onClick={reset} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed">
        {previewData.refined_prompt}
      </p>

      {previewData.notes && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          {previewData.notes}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={reset}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-800/60"
        >
          <X className="w-3.5 h-3.5" />
          Keep my version
        </button>
        <button
          onClick={handleAccept}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          <Check className="w-3.5 h-3.5" />
          Use this
        </button>
      </div>
    </div>
  ) : null;

  return {
    stage,
    isPending: refineMutation.isPending,
    canOpen: Boolean(draft && draft.trim()) && !refineMutation.isPending,
    open,
    cancel: reset,
    modal,
    preview,
  };
};

const PromptRefiner = ({ draft, mode = 'creative_studio', aspectRatio, recentMessages, onAccept, disabled }) => {
  const { stage, isPending, canOpen, open, modal, preview } = usePromptRefiner({
    draft, mode, aspectRatio, recentMessages, onAccept,
  });

  if (stage === 'idle') {
    return (
      <button
        type="button"
        onClick={() => open()}
        disabled={disabled || !canOpen}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md
                   bg-gradient-to-r from-blue-50 to-orange-50 dark:from-blue-900/30 dark:to-orange-900/30
                   text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50
                   hover:from-blue-100 hover:to-orange-100 dark:hover:from-blue-900/50 dark:hover:to-orange-900/50
                   disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        title="Refine with JPRO Intelligence"
      >
        {isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
        {isPending ? 'Thinking…' : 'Refine with JPRO Intelligence'}
      </button>
    );
  }

  return stage === 'questions' ? modal : preview;
};

export default PromptRefiner;
